/**
 * resolveHeadingChrome — the SINGLE, pure, render-agnostic source for the
 * section-heading treatment derived from `theme.headingScale`.
 *
 * RC3/RC5 (report-layout-rebuild W1): the on-screen viewer historically owned
 * the heading size/weight/rule/spacing map inline (`reportSectionHeadingChrome`
 * in SiteRagReportView). DOCX + PPTX re-derived their own heading treatment, so
 * a preset's heading scale silently diverged at the deliverable. This helper
 * extracts the keyed-on-`headingScale` decision into ONE place so the screen,
 * the parity gate, and (later, W5) the DOCX/PPTX exporters all read the SAME
 * heading treatment off the contract (`presentation.headingChrome`).
 *
 * The output is medium-NEUTRAL: it carries the abstract decisions (the resolved
 * scale, the relative size step, the rule weight/style, the case + tracking)
 * plus the resolved colours/font, so a CSS renderer maps it to Tailwind classes
 * and a Word/PPTX renderer maps it to point sizes and border specs WITHOUT
 * re-deriving the policy. The on-screen viewer adapts this into its existing
 * `{ h2Class, textClass, h2Style }` shape (byte-identical to the legacy inline
 * map) so nothing changes visually on screen.
 *
 * Pure / side-effect-free so it is safe in render and unit-testable.
 */
import type { ReportTheme } from '../types/reportTheme';

export type HeadingScale = ReportTheme['headingScale'];

/** Relative size step for a section head (maps to text-2xl / text-xl / text-sm
 *  on screen, and to a point size in DOCX/PPTX). */
export type HeadingSizeStep = 'display' | 'reference' | 'micro';

/** Rule (bottom border) weight under the section head. */
export type HeadingRuleWeight = 'hairline' | 'medium';

export interface ResolvedHeadingChrome {
  /** The resolved heading scale after clamping (editorial | technical | compact). */
  scale: HeadingScale;
  /** Relative size step. */
  sizeStep: HeadingSizeStep;
  /** Render the head in UPPERCASE with wide tracking (conference-paper voice). */
  uppercase: boolean;
  /** Letter-spacing token. 'tight' for display heads, 'wide' for compact caps,
   *  'normal' for the reference treatment. */
  tracking: 'tight' | 'normal' | 'wide';
  /** Bottom-rule weight under the head. */
  ruleWeight: HeadingRuleWeight;
  /**
   * Which colour the bottom rule uses:
   *   - 'accent'  → editorial hairline uses the brand accent role,
   *   - 'heading' → technical/compact rules use the heading colour.
   * The renderer resolves this to a concrete colour (screen uses roleColor /
   * theme.headingColor; export maps the same way) so the policy lives here.
   */
  ruleColorRole: 'accent' | 'heading';
  /** Resolved heading colour (CSS / hex). */
  headingColor: string;
  /** Resolved heading font family (CSS stack / family name). */
  headingFont: string;
}

/**
 * Clamp a (possibly stale / undefined) `headingScale` to the documented set.
 * Mirrors the viewer's `resolveReportHeadingScale` so the two never drift.
 */
export function resolveHeadingScale(scale: HeadingScale | undefined): HeadingScale {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

/**
 * resolveHeadingChrome — the keyed-on-`headingScale` heading-treatment policy.
 *
 *   - editorial (Journal / Quarterly / Monograph — journal voice): a large
 *     display head over a THIN brand-accent hairline.
 *   - technical (Institutional): the balanced reference treatment — a crisp
 *     medium heading-colour rule (the original legacy look).
 *   - compact (Review / Proceedings — IEEE/ACM density): a small, UPPERCASE,
 *     wide-tracked head over a tight thin heading-colour rule.
 *
 * COLOUR + FONT always come from `theme.headingColor` / `theme.headingFont`
 * (the rule colour is selected via `ruleColorRole`); only the size/weight/
 * case/spacing vary by scale.
 */
export function resolveHeadingChrome(
  theme: Pick<ReportTheme, 'headingScale' | 'headingColor' | 'headingFont'>,
): ResolvedHeadingChrome {
  const scale = resolveHeadingScale(theme.headingScale);
  const headingColor = theme.headingColor;
  const headingFont = theme.headingFont;

  switch (scale) {
    case 'editorial':
      return {
        scale,
        sizeStep: 'display',
        uppercase: false,
        tracking: 'tight',
        ruleWeight: 'hairline',
        ruleColorRole: 'accent',
        headingColor,
        headingFont,
      };
    case 'compact':
      return {
        scale,
        sizeStep: 'micro',
        uppercase: true,
        tracking: 'wide',
        ruleWeight: 'hairline',
        ruleColorRole: 'heading',
        headingColor,
        headingFont,
      };
    case 'technical':
    default:
      return {
        scale,
        sizeStep: 'reference',
        uppercase: false,
        tracking: 'normal',
        ruleWeight: 'medium',
        ruleColorRole: 'heading',
        headingColor,
        headingFont,
      };
  }
}
